"use client";

import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { rem } from "@huub-web/utils";
import {
  Button,
  Flex,
  Icon,
  Image,
  Message,
  Modal,
  Placeholder,
  Text,
} from "@huub-web/ui";
import { useOcktoQR } from "../../../hooks";
import {
  modalContentStyle,
  qrContainerStyle,
  qrPanelStyle,
} from "../OcktoCardTemplate.css";

type OcktoModalProps = {
  /**
   * API endpoint for fetching the QR code
   */
  endpoint: string;
  /**
   * Modal header title
   */
  modalTitle: string;
  /**
   * Description text shown on the left side
   */
  modalDescription: string;
  /**
   * Info box message text
   */
  modalInfoText: string;
  /**
   * Whether the UI is locked
   */
  lockUI: boolean;
  /**
   * Callback when the modal is closed
   */
  onClose: () => void;
};

/**
 * Modal that displays the Ockto QR code with description and info box.
 * QR code is fetched on mount and can be manually refreshed.
 */
export const OcktoModal: FC<OcktoModalProps> = ({
  endpoint,
  modalTitle,
  modalDescription,
  modalInfoText,
  lockUI,
  onClose,
}) => {
  const { t } = useTranslation();
  const { src, loading, handleRefresh } = useOcktoQR({ endpoint });

  return (
    <Modal
      title={
        <Text variant="displayS" bold>
          {modalTitle}
        </Text>
      }
      size="large"
      placement="center"
      closeable
      onClose={onClose}
      escape
    >
      <div className={modalContentStyle}>
        <Flex direction="column" gap="s" grow basis="50%">
          <Text variant="bodyM" color="secondary">
            {modalDescription}
          </Text>
          {modalInfoText && <Message variant="default" body={modalInfoText} />}
        </Flex>

        <Flex
          direction="column"
          gap="s"
          align="center"
          justify="center"
          padding="m"
          grow
          basis="50%"
          className={qrPanelStyle}
        >
          <Image
            src="/assets/images/ockto.png"
            alt="Ockto"
            style={{ height: rem(33), width: rem(119), objectFit: "contain" }}
            placeholder={false}
          />
          <Flex
            align="center"
            justify="center"
            padding="xs"
            className={qrContainerStyle}
          >
            <div style={{ width: rem(184), aspectRatio: 1 }}>
              {loading ? (
                <Placeholder
                  variant="loadingShade"
                  size="xs"
                  height="100%"
                  width="100%"
                >
                  <Image
                    src={src}
                    alt={t("ocktoCard.qrCodeAlt")}
                    height="100%"
                    width="100%"
                  />
                </Placeholder>
              ) : (
                src && (
                  <Image
                    src={src}
                    alt={t("ocktoCard.qrCodeAlt")}
                    height="100%"
                    width="100%"
                  />
                )
              )}
            </div>
          </Flex>
          <Button
            variant="primary"
            leading={<Icon icon="reload" size="s" />}
            onClick={handleRefresh}
            disabled={loading || lockUI}
          >
            {t("ocktoCard.refreshButton")}
          </Button>
        </Flex>
      </div>
    </Modal>
  );
};
